import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { superAdmin as api } from "@/lib/api/super";
import { useAuth } from "./useAuth";

export type TelemetryEvent = {
  readonly category: "view" | "ui";
  readonly action: string;
  readonly data?: Record<string, string | number | boolean>;
};

type Queued = TelemetryEvent & { readonly ts: string };

const VIEWS: readonly (readonly [string, string])[] = [
  ["/workspace/workbench", "simulator"],
  ["/workspace/backtest", "backtest"],
  ["/workspace/journal", "journal"],
  ["/workspace/alerts", "alerts"],
  ["/workspace/me/library", "library"],
  ["/workspace/explore", "guide"],
  ["/workspace/settings", "settings"],
  ["/purchase", "purchase"],
  ["/workspace", "workspace"],
];

const FLUSH_MS = 15_000;
const CAP = 50;

const TelemetryContext = createContext<(e: TelemetryEvent) => void>(() => {});

export function useTelemetry(): (e: TelemetryEvent) => void {
  return useContext(TelemetryContext);
}

export function TelemetryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const queue = useRef<Queued[]>([]);
  const signedIn = Boolean(user);

  const flush = useCallback((unloading: boolean) => {
    const events = queue.current.splice(0, CAP);
    if (events.length === 0) return;
    void api.sendTelemetry(events, unloading);
  }, []);

  const track = useCallback(
    (e: TelemetryEvent) => {
      if (!signedIn) return;
      queue.current.push({ ...e, ts: new Date().toISOString() });
      if (queue.current.length >= CAP) flush(false);
    },
    [signedIn, flush],
  );

  useEffect(() => {
    if (!signedIn) return;
    const timer = setInterval(() => flush(false), FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") flush(true);
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      flush(true);
    };
  }, [signedIn, flush]);

  useEffect(() => {
    const hit = VIEWS.find(([prefix]) => pathname.startsWith(prefix));
    if (hit) track({ action: hit[1], category: "view" });
  }, [pathname, track]);

  return (
    <TelemetryContext.Provider value={track}>
      {children}
    </TelemetryContext.Provider>
  );
}
